/* eslint-disable */
import { useEffect, useState } from 'react'
import { Control, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import { ContainerGroupField } from '@/components/common/ContainerGroupField'
import { AutocompleteControl } from '@/components/form-controls'
import { useAuthHook } from '@/hooks'
import { useAspirantsApi } from '@/services/api/aspirantsApi'
import { useResourcesApi } from '@/services/api/resourcesApi'
import { ErrorHandlingStrategy, MessageLevel } from '@/types/api'
import { BloodTypesLabels } from '@/types/bloodTypes/enums'
import { AboutYouFormData } from '@/types/forms/aboutYouForm'

interface MedicalControlProps {
    control: Control<AboutYouFormData>
    trigger?: any
}

export const MedicalControl = ({
    control,
    trigger
}: MedicalControlProps): JSX.Element => {
    const { t } = useTranslation()
    const { idAspirant } = useAuthHook()

    const bloodTypeOptions = Object.entries(BloodTypesLabels).map(
        ([key, value]) => ({
            value: key,
            label: value
        })
    )
    const resourcesApi = useResourcesApi({
        messageLevel: MessageLevel.ERRORS_AND_INFO_ONLY,
        errorHandling: ErrorHandlingStrategy.RETURN
    })
    const [hasFetched, setHasFetched] = useState(false)
    const [epsList, setEpsList] = useState<any[]>([])
    const [bloodTypes, setBloodTypes] = useState<any[]>([])

    useEffect(() => {
        let mounted = true

        if (hasFetched) {
            return
        }

        const fetchAllOptions = async () => {
            try {
                const [epsList, bloodTypes] = await Promise.all([
                    resourcesApi.getEpsList(),
                    resourcesApi.getBloodTypes()
                ])
                // Para filtrar duplicados y mantener solo la primera ocurrencia:
                const uniqueEpsList = epsList.filter(
                    (item, index, self) =>
                        index === self.findIndex((t) => t.value === item.value)
                )

                if (!mounted) {
                    return
                }

                setEpsList(
                    Array.isArray(uniqueEpsList) && uniqueEpsList.length > 0
                        ? uniqueEpsList
                            .filter((item) => item?.value && item?.label)
                            .map((item, index) => ({
                                key: `type-${index}`,
                                value: item.value,
                                label: item.label
                            }))
                        : []
                )

                setBloodTypes(
                    Array.isArray(bloodTypes) && bloodTypes.length > 0
                        ? bloodTypes
                            .filter((item) => item?.value && item?.label)
                            .map((item, index) => ({
                                key: `type-${item.value}-${index}`,
                                value: item.value,
                                label: item.label
                            }))
                        : []
                )

                setHasFetched(true)
            } catch (error) {
                console.error('Failed to fetch resources:', error)
            }
        }

        fetchAllOptions()
    }, [resourcesApi])

    return (
        <ContainerGroupField tittleGroup={t('forms.groups.medicalInformation')}>
            <AutocompleteControl
                name="eps"
                control={control}
                label={t('medicalInformation.eps')}
                options={epsList}
                rules={{
                    required: t('medicalInformation.required.epsRequired')
                }}
                onChangeCustom={() => {
                    if (trigger) {
                        trigger('eps')
                    }
                }}
            />
            <AutocompleteControl
                name="bloodType"
                control={control}
                label={t('medicalInformation.bloodType')}
                options={bloodTypeOptions}
                rules={{
                    required: t('medicalInformation.required.bloodTypeRequired')
                }}
                onChangeCustom={() => {
                    if (trigger) {
                        trigger('bloodType')
                    }
                }}
            />
        </ContainerGroupField>
    )
}
